import React, { useState } from 'react';
import { BookOpen, Loader2, Maximize2, Minimize2 } from 'lucide-react';

/**
 * Shared interactive book/PDF viewer used by MenschenA1, A12, A21, A22.
 */
export const InteractiveBookViewer = ({ books, courseTitle, isActive }) => {
  const [pdfFullscreen, setPdfFullscreen] = useState(false);
  const [bookUrl, setBookUrl] = useState(null);
  const [bookLoading, setBookLoading] = useState(false);
  const [selectedBook, setSelectedBook] = useState(Object.keys(books)[0]);
  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchBookUrl = async (bookType) => {
    const book = books[bookType || selectedBook];
    setBookLoading(true);
    setBookUrl(null);
    try {
      const res = await fetch(`${API}/api/books/${book.id}/url`);
      const data = await res.json();
      setBookUrl(data.viewer_url || book.url);
    } catch {
      setBookUrl(book.url);
    } finally {
      setBookLoading(false);
    }
  };

  if (!isActive) return null;

  const currentBook = books[selectedBook];

  return (
    <div className={`section active ${pdfFullscreen ? 'pdf-fullscreen' : ''}`} data-testid="tab-pdf">
      <div className="pdf-header">
        <div className="pdf-header-left">
          <BookOpen className="h-5 w-5" style={{ color: '#f59e0b' }} />
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#e2e8f0', margin: 0 }}>Interaktives Lernen – {courseTitle}</h2>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '2px 0 0' }}>Interactive Textbook · الكتاب التفاعلي</p>
          </div>
        </div>
        <div className="pdf-actions">
          {bookUrl && (
            <>
              <button className="pdf-action-btn" onClick={() => setPdfFullscreen(!pdfFullscreen)} title={pdfFullscreen ? 'Exit Fullscreen' : 'Fullscreen'} data-testid="pdf-fullscreen-toggle">
                {pdfFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
              <a href={bookUrl} target="_blank" rel="noopener noreferrer" className="pdf-action-btn" title="Open in new tab" data-testid="pdf-new-tab">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </a>
            </>
          )}
        </div>
      </div>

      {/* Book Type Selector */}
      <div style={{ display: 'flex', gap: '8px', padding: '12px 20px', background: '#0f172a', borderLeft: '1px solid rgba(245,158,11,0.2)', borderRight: '1px solid rgba(245,158,11,0.2)' }}>
        {Object.entries(books).map(([key, book]) => (
          <button key={key} onClick={() => { setSelectedBook(key); setBookUrl(null); }}
            style={{ padding: '8px 20px', borderRadius: '8px', border: selectedBook === key ? '2px solid #f59e0b' : '1px solid #334155', background: selectedBook === key ? 'rgba(245,158,11,0.15)' : '#1e293b', color: selectedBook === key ? '#f59e0b' : '#94a3b8', fontWeight: 600, fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s' }}
            data-testid={`book-select-${key}`}>
            {key === 'kursbuch' ? '📕' : '📗'} {book.label} ({book.labelEn})
          </button>
        ))}
      </div>

      {!bookUrl && !bookLoading && (
        <div className="pdf-fallback">
          <BookOpen className="h-12 w-12" style={{ color: '#f59e0b', marginBottom: '16px' }} />
          <p style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '17px', marginBottom: '8px' }}>{currentBook.label} – {courseTitle}</p>
          <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '20px' }}>Klicken Sie, um das {currentBook.label} zu laden.</p>
          <button onClick={() => fetchBookUrl(selectedBook)} className="pdf-open-btn" data-testid="pdf-load-book">
            <BookOpen className="h-4 w-4" /> {currentBook.label} laden
          </button>
        </div>
      )}

      {bookLoading && (
        <div className="pdf-fallback">
          <Loader2 className="h-10 w-10 animate-spin" style={{ color: '#f59e0b', marginBottom: '16px' }} />
          <p style={{ color: '#e2e8f0', fontWeight: 600 }}>Buch wird geladen...</p>
        </div>
      )}

      {bookUrl && (
        <div className="pdf-iframe-container">
          <iframe src={bookUrl} title={`${courseTitle} ${currentBook.label}`} className="pdf-iframe" allow="fullscreen" data-testid="pdf-iframe" />
        </div>
      )}
    </div>
  );
};
